import { Route, Routes } from "react-router-dom";
import { lazy, memo, Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import MModalError from "@components/common/m-modal-error/MModalError";
import MModalSuccess from "@components/common/m-modal-success/MModalSuccess";
import Spinner from "@components/common/spinner/spinner";
import RequireAuth, { RequireUnAuth } from "@components/app/PrivateRoute";
import Layout from "@components/layout/Layout";
import ErrorBoundary from "@components/app/ErrorBoundary";
import TextToSpeech from "@components/common/TextToSpeech/TextToSpeech";
import AIChatWidget from "@components/common/ai-chat/AIChatWidget";

import { setIsError, setIsSuccess } from "@reducers/notificationSlice";

import { useParam } from "@utils/hooks/useParam";

import "./App.css";
import { USER_ROLES } from "@models/users";
import { ConfirmProvider } from "@utils/hooks/useCustomConfirm";

const PublicationParagraphTaskCollection = lazy(() =>
  import(
    "./views/PublicationParagraphTaskCollection/PublicationParagraphTaskCollection"
  ),
);
const Library = lazy(() => import("./views/library/Library"));
const Search = lazy(() => import("./views/search/Search"));
const Eula = lazy(() => import("./views/eula/Eula"));
const Privacy = lazy(() => import("./views/privacy/Privacy"));
const CookiesPage = lazy(() => import("./views/cookies/Cookies"));
const Packages = lazy(() => import("./views/packages/Packages"));
const Book = lazy(() => import("./components/book/Book"));
const Faq = lazy(() => import("./views/faq/FAQ"));
const PackagePage = lazy(() => import("./components/package/PackagePage"));
const PassChange = lazy(() => import("./views/passChange/PassChange"));
const ConfirmationRegistration = lazy(() =>
  import("./views/confirmation/ConfirmationRegistration"),
);
const ConfirmationReset = lazy(() =>
  import("./views/confirmation/ConfirmationReset"),
);
const PrivateUserAdvantage = lazy(() =>
  import("./views/advantages/PrivateUserAdvantage"),
);
const SchoolAdvantage = lazy(() =>
  import("./views/advantages/SchoolAdvantage"),
);
const TeacherAdvantage = lazy(() =>
  import("./views/advantages/TeacherAdvantage"),
);
const StudentAdvantage = lazy(() =>
  import("./views/advantages/StudentAdvantage"),
);
const Support = lazy(() => import("./views/support/support"));
const Users = lazy(() => import("./views/users/Users"));
const UserView = lazy(() => import("./views/users/UserView"));
const SuperAdmin = lazy(() => import("./views/superAdmin/SuperAdmin"));
const Schools = lazy(() => import("./views/schools/Schools"));
const SchoolView = lazy(() => import("./views/schools/SchoolView"));
const UserEdit = lazy(() => import("./views/users/UserEdit"));
const Subscriptions = lazy(() => import("./views/subscriptions/Subscriptions"));
const SubscriptionAdd = lazy(() =>
  import("./views/subscriptions/SubscriptionAdd"),
);
const Create = lazy(() => import("./views/superAdmin/Create"));
const SchoolEdit = lazy(() => import("./views/schools/SchoolEdit"));
const Student = lazy(() => import("./views/student/Student"));
const StudentsWorks = lazy(() => import("./views/studentsWorks/StudentsWorks"));
const MyTextbooks = lazy(() => import("./views/myTextbooks/МyTextbooks"));
const Teacher = lazy(() => import("./views/teacher/Teacher"));
const Settings = lazy(() => import("./views/settings/Settings"));
const PrivateUser = lazy(() => import("./views/privateUser/PrivateUser"));
const SchoolAdmin = lazy(() => import("./views/schoolAdmin/SchoolAdmin"));
const SchoolUsers = lazy(() => import("./views/school-users/SchoolUsers"));
const MainPage = lazy(() => import("./views/main-page/MainPage"));
const PublicationContentView = lazy(() =>
  import("./views/PublicationContentView/PublicationContentView"),
);
const ClassRegisters = lazy(() =>
  import("./views/classRegisters/ClassRegisters"),
);
const SchoolAdminSubs = lazy(() =>
  import("./views/SchoolAdminSubs/SchoolAdminSubs"),
);
const SchoolAdminCreate = lazy(() =>
  import("./views/school-admin-create/SchoolAdminCreate"),
);
const SchoolAdminSubsAdd = lazy(() =>
  import("./views/SchoolAdminSubsAdd/SchoolAdminSubsAdd"),
);
const PublicationParagraphView = lazy(() =>
  import("./views/PublicationParagraphView/PublicationParagraphView"),
);
const ClassRegisterView = lazy(() =>
  import("./views/classRegisters/ClassRegisterView"),
);
const TaskModule = lazy(() => import("./views/task/TaskModule"));
const StudentWorkPage = lazy(() =>
  import(
    "./components/class-register/ClassRegisterWorks/components/StudentWorkPage/StudentWorkPage"
  ),
);

function App() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { getUrlParam } = useParam();

  const isGlobalLoading = useSelector((state) => state.spinner.loading);

  useEffect(() => {
    const is_success = getUrlParam("is_success");
    if (is_success === "true") {
      dispatch(
        setIsSuccess({
          successText: [...Array(7)].map((_, index) =>
            t(`general.paySystem.${index + 1}`),
          ),
          successNavigation: "/library",
          successButtonName: t("general.modals.purchaseModal.goToLibrary"),
        }),
      );
    } else if (is_success === "false") {
      dispatch(setIsError(t(`general.paySystem.error`)));
    }
  }, []);

  return (
    <ErrorBoundary>
      <TextToSpeech />
      <Spinner spinning={isGlobalLoading} fullScreen={true} />
      <Suspense fallback={<Layout />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route
              index
              element={
                <RequireUnAuth>
                  <MainPage />
                </RequireUnAuth>
              }
            />
            <Route path="library" element={<Library />} />
            <Route path="library/:id" element={<Book />} />
            <Route
              path="library/new/:id"
              element={<PublicationContentView />}
            />
            <Route
              path="library/new/:bookId/paragraph/:id/view"
              element={<PublicationParagraphView />}
            />
            <Route
              path="library/new/:bookId/paragraph/:id/taskCollection"
              element={
                <RequireAuth
                  roles={[USER_ROLES.SUPERADMIN, USER_ROLES.TEACHER]}
                >
                  <PublicationParagraphTaskCollection />
                </RequireAuth>
              }
            />
            <Route path="search" element={<Search />} />
            <Route path="packages" element={<Packages />} />
            <Route path="package" element={<PackagePage />} />
            <Route path="advantages/school" element={<SchoolAdvantage />} />
            <Route path="advantages/teacher" element={<TeacherAdvantage />} />
            <Route path="advantages/student" element={<StudentAdvantage />} />
            <Route
              path="advantages/privateUser"
              element={<PrivateUserAdvantage />}
            />
            <Route path="faq" element={<Faq />} />
            <Route path="eula" element={<Eula />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="cookies" element={<CookiesPage />} />
            <Route path="support" element={<Support />} />
            <Route
              path="settings"
              element={
                <RequireAuth>
                  <Settings />
                </RequireAuth>
              }
            />
            <Route
              path="change-password"
              element={
                <RequireAuth>
                  <PassChange />
                </RequireAuth>
              }
            />
            <Route
              path="confirmation-registration"
              element={<ConfirmationRegistration />}
            />
            <Route path="confirmation-reset" element={<ConfirmationReset />} />
            <Route
              path="users"
              element={
                <RequireAuth roles={[USER_ROLES.SUPERADMIN]}>
                  <Users />
                </RequireAuth>
              }
            />
            <Route
              path="users/:username/:role"
              element={
                <RequireAuth
                  roles={[USER_ROLES.SUPERADMIN, USER_ROLES.SCHOOL_ADMIN]}
                >
                  <UserView />
                </RequireAuth>
              }
            />
            <Route
              path="create/:type"
              element={
                <RequireAuth roles={[USER_ROLES.SUPERADMIN]}>
                  <Create />
                </RequireAuth>
              }
            />
            <Route
              path="users/edit/:username/:role"
              element={
                <RequireAuth
                  roles={[USER_ROLES.SUPERADMIN, USER_ROLES.SCHOOL_ADMIN]}
                >
                  <UserEdit />
                </RequireAuth>
              }
            />
            <Route
              path="subscriptions"
              element={
                <RequireAuth roles={[USER_ROLES.SUPERADMIN]}>
                  <Subscriptions />
                </RequireAuth>
              }
            />
            <Route
              path="subscriptions/add"
              element={
                <RequireAuth roles={[USER_ROLES.SUPERADMIN]}>
                  <SubscriptionAdd />
                </RequireAuth>
              }
            />
            <Route
              path="superadmin"
              element={
                <RequireAuth roles={[USER_ROLES.SUPERADMIN]}>
                  <SuperAdmin />
                </RequireAuth>
              }
            />
            <Route
              path="schools"
              element={
                <RequireAuth roles={[USER_ROLES.SUPERADMIN]}>
                  <Schools />
                </RequireAuth>
              }
            />
            <Route
              path="schools/:id"
              element={
                <RequireAuth roles={[USER_ROLES.SUPERADMIN]}>
                  <SchoolView />
                </RequireAuth>
              }
            />
            <Route
              path="schools/edit/:id"
              element={
                <RequireAuth roles={[USER_ROLES.SUPERADMIN]}>
                  <SchoolEdit />
                </RequireAuth>
              }
            />
            <Route
              path="student"
              element={
                <RequireAuth roles={[USER_ROLES.STUDENT]}>
                  <Student />
                </RequireAuth>
              }
            />
            <Route
              path="student/works/:id"
              element={
                <RequireAuth roles={[USER_ROLES.STUDENT]}>
                  <StudentsWorks />
                </RequireAuth>
              }
            />
            <Route
              path="student/works/:id/task/:task_id"
              element={
                <RequireAuth roles={[USER_ROLES.STUDENT]}>
                  <ConfirmProvider>
                    <TaskModule />
                  </ConfirmProvider>
                </RequireAuth>
              }
            />
            <Route
              path="student/works/work-info/:work_id"
              element={
                ///student/works/work-info/${record.student_work_id}
                <RequireAuth roles={[USER_ROLES.STUDENT]}>
                  <StudentWorkPage />
                </RequireAuth>
              }
            />
            <Route
              path="private_user"
              element={
                <RequireAuth roles={[USER_ROLES.PRIVATE_USER]}>
                  <PrivateUser />
                </RequireAuth>
              }
            />
            <Route
              path="textbooks"
              element={
                <RequireAuth
                  roles={[
                    USER_ROLES.PRIVATE_USER,
                    USER_ROLES.TEACHER,
                    USER_ROLES.STUDENT,
                  ]}
                >
                  <MyTextbooks />
                </RequireAuth>
              }
            />
            <Route
              path="teacher"
              element={
                <RequireAuth roles={[USER_ROLES.TEACHER]}>
                  <Teacher />
                </RequireAuth>
              }
            />
            <Route
              path="magazines"
              element={
                <RequireAuth
                  roles={[USER_ROLES.TEACHER, USER_ROLES.SCHOOL_ADMIN]}
                >
                  <ClassRegisters />
                </RequireAuth>
              }
            />
            <Route
              path="magazines/:id"
              element={
                <RequireAuth
                  roles={[USER_ROLES.TEACHER, USER_ROLES.SCHOOL_ADMIN]}
                >
                  <ClassRegisterView />
                </RequireAuth>
              }
            />
            <Route
              path="school_admin"
              element={
                <RequireAuth roles={[USER_ROLES.SCHOOL_ADMIN]}>
                  <SchoolAdmin />
                </RequireAuth>
              }
            />
            <Route
              path="school_admin/subscriptions"
              element={
                <RequireAuth roles={[USER_ROLES.SCHOOL_ADMIN]}>
                  <SchoolAdminSubs />
                </RequireAuth>
              }
            />
            <Route
              path="school_admin/subscriptions/add/:id"
              element={
                <RequireAuth roles={[USER_ROLES.SCHOOL_ADMIN]}>
                  <SchoolAdminSubsAdd />
                </RequireAuth>
              }
            />
            <Route
              path="school_admin/users"
              element={
                <RequireAuth roles={[USER_ROLES.SCHOOL_ADMIN]}>
                  <SchoolUsers />
                </RequireAuth>
              }
            />
            <Route
              path="school_admin/create/user"
              element={
                <RequireAuth roles={[USER_ROLES.SCHOOL_ADMIN]}>
                  <SchoolAdminCreate />
                </RequireAuth>
              }
            />
            <Route
              path="*"
              element={
                <RequireUnAuth>
                  <MainPage />
                </RequireUnAuth>
              }
            />
          </Route>
        </Routes>
        <MModalError />
        <MModalSuccess />
        <AIChatWidget />
      </Suspense>
    </ErrorBoundary>
  );
}

export default memo(App);
